import { PurchasingRepository } from '@Purchasing/Data/Repository/PurchasingRepository'
import {
    CreatePurchaseProcessUseCase,
    GetPurchaseProcessAuditUseCase,
    GetPurchaseProcessByIdUseCase,
    GetPurchaseProcessesUseCase,
    GetPurchasingStagesUseCase,
    GetSourceContextUseCase,
    UpdatePurchaseProcessAssigneeUseCase,
    UpdatePurchaseProcessStageUseCase,
    UpdatePurchaseProcessStatusUseCase,
    UpdatePurchaseProcessUseCase,
    GetSupplierQuotesUseCase,
    CreateSupplierQuoteUseCase,
    UpdateSupplierQuoteUseCase,
    UpdateSupplierQuoteStatusUseCase,
    SelectSupplierQuoteUseCase,
    DeleteSupplierQuoteUseCase,
    GetPurchasingSuppliersUseCase,
    SearchPurchasingSuppliersUseCase,
    CreatePurchasingSupplierUseCase,
    UpdatePurchasingSupplierUseCase,
    GetPurchasePreparationUseCase,
    CreateProcessesBatchUseCase,
    GetPurchaseByProcessUseCase,
    CreatePurchaseUseCase,
    UpdatePurchaseUseCase,
    ChangePurchaseStatusUseCase,
    GetPurchaseFinanceUseCase,
    CreatePurchasePaymentUseCase,
    UpdatePurchasePaymentUseCase,
    MarkPurchasePaymentPaidUseCase,
    CancelPurchasePaymentUseCase,
    GetProcessAttachmentsUseCase,
    InitiateAttachmentUseCase,
    CompleteAttachmentUseCase,
    UpdateAttachmentUseCase,
    DeleteAttachmentUseCase,
    GetProductionLinksUseCase,
    ConfirmProductionLinkUseCase,
    UnlinkProductionLinkUseCase,
    RefreshProductionUseCase,
} from '@Purchasing/Domain/UseCases'
import { Container } from 'inversify'
import { PurchasingAPI } from '../API/PurchasingAPI'
import { Register } from './PurchasingRegister'

const container = new Container()

const bindUseCase = <T>(
    identifier: symbol | (new (...args: any[]) => T),
    useCaseFactory: () => T
) => {
    container
        .bind<T>(identifier as any)
        .toDynamicValue(useCaseFactory)
        .inSingletonScope()
}

const makeRepository = () => new PurchasingRepository(new PurchasingAPI())

bindUseCase(
    Register.UseCaseGetPurchaseProcesses,
    () =>
        new GetPurchaseProcessesUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseGetPurchaseProcessById,
    () =>
        new GetPurchaseProcessByIdUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseCreatePurchaseProcess,
    () =>
        new CreatePurchaseProcessUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseUpdatePurchaseProcess,
    () =>
        new UpdatePurchaseProcessUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseUpdatePurchaseProcessStatus,
    () =>
        new UpdatePurchaseProcessStatusUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseUpdatePurchaseProcessStage,
    () =>
        new UpdatePurchaseProcessStageUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseUpdatePurchaseProcessAssignee,
    () =>
        new UpdatePurchaseProcessAssigneeUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseGetPurchaseProcessAudit,
    () =>
        new GetPurchaseProcessAuditUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseGetPurchasingStages,
    () =>
        new GetPurchasingStagesUseCase(
            new PurchasingRepository(new PurchasingAPI())
        )
)
bindUseCase(
    Register.UseCaseGetSourceContext,
    () => new GetSourceContextUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseGetSupplierQuotes,
    () => new GetSupplierQuotesUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseCreateSupplierQuote,
    () => new CreateSupplierQuoteUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseUpdateSupplierQuote,
    () => new UpdateSupplierQuoteUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseUpdateSupplierQuoteStatus,
    () => new UpdateSupplierQuoteStatusUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseSelectSupplierQuote,
    () => new SelectSupplierQuoteUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseDeleteSupplierQuote,
    () => new DeleteSupplierQuoteUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseGetPurchasingSuppliers,
    () => new GetPurchasingSuppliersUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseSearchPurchasingSuppliers,
    () => new SearchPurchasingSuppliersUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseCreatePurchasingSupplier,
    () => new CreatePurchasingSupplierUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseUpdatePurchasingSupplier,
    () => new UpdatePurchasingSupplierUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseGetProcessAttachments,
    () => new GetProcessAttachmentsUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseInitiateAttachment,
    () => new InitiateAttachmentUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseCompleteAttachment,
    () => new CompleteAttachmentUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseUpdateAttachment,
    () => new UpdateAttachmentUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseDeleteAttachment,
    () => new DeleteAttachmentUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseGetProductionLinks,
    () => new GetProductionLinksUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseConfirmProductionLink,
    () => new ConfirmProductionLinkUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseUnlinkProductionLink,
    () => new UnlinkProductionLinkUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseRefreshProduction,
    () => new RefreshProductionUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseGetPurchaseFinance,
    () => new GetPurchaseFinanceUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseCreatePurchasePayment,
    () => new CreatePurchasePaymentUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseUpdatePurchasePayment,
    () => new UpdatePurchasePaymentUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseMarkPurchasePaymentPaid,
    () => new MarkPurchasePaymentPaidUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseCancelPurchasePayment,
    () => new CancelPurchasePaymentUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseGetPurchaseByProcess,
    () => new GetPurchaseByProcessUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseCreatePurchase,
    () => new CreatePurchaseUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseUpdatePurchase,
    () => new UpdatePurchaseUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseChangePurchaseStatus,
    () => new ChangePurchaseStatusUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseGetPurchasePreparation,
    () => new GetPurchasePreparationUseCase(makeRepository())
)
bindUseCase(
    Register.UseCaseCreateProcessesBatch,
    () => new CreateProcessesBatchUseCase(makeRepository())
)

export default container
